import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog';
import { useMcpConnectionsQuery } from '@/hooks/queries/use-mcp-query';
import { Button, HStack, Spinner, Stack, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
  ASSISTANT_PROVIDERS,
  assistantConnectionSummary,
  type ConnectableAssistant,
} from './assistant-providers';
import { AssistantConnectSuccess } from './AssistantConnectSuccess';
import { ChatGptConnectSteps } from './ChatGptConnectSteps';
import { ClaudeConnectSteps } from './ClaudeConnectSteps';

/** How often an open dialog looks for the connection the brand is finishing in the other tab. */
const CONNECTION_POLL_MS = 4000;

interface AssistantConnectDialogProps {
  provider: ConnectableAssistant;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * The connection itself happens in the assistant and ends on Fluenceur's consent screen,
 * so this dialog only sends the brand to the right place, then watches for the new
 * connection and says so the moment it lands.
 */
export const AssistantConnectDialog: React.FC<AssistantConnectDialogProps> = ({
  provider,
  open,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const { name } = ASSISTANT_PROVIDERS[provider];
  const { data } = useMcpConnectionsQuery({ refetchInterval: open ? CONNECTION_POLL_MS : false });
  const { isConnected } = assistantConnectionSummary(data?.connections, provider);

  return (
    <DialogRoot
      open={open}
      onOpenChange={(details) => onOpenChange(details.open)}
      placement="center"
    >
      <DialogContent rounded="2xl" maxW="lg">
        <DialogHeader>
          <DialogTitle>{t('integrations.assistants.dialog.title', { name })}</DialogTitle>
        </DialogHeader>
        <DialogCloseTrigger />
        <DialogBody>
          {isConnected ? (
            <AssistantConnectSuccess name={name} />
          ) : (
            <Stack gap={5}>
              {provider === 'claude' ? <ClaudeConnectSteps /> : <ChatGptConnectSteps />}
              <HStack gap={2} color="fg.muted">
                <Spinner size="xs" />
                <Text fontSize="xs">{t('integrations.assistants.dialog.waiting')}</Text>
              </HStack>
            </Stack>
          )}
        </DialogBody>
        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button variant={isConnected ? 'solid' : 'outline'}>
              {isConnected ? t('integrations.assistants.dialog.finish') : t('common.close')}
            </Button>
          </DialogActionTrigger>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};
